import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { COLORS, SPACING } from './designSystem/tokens';
import { PdfHeader, PdfFooter, PdfSectionTitle, PdfInfoColumns, PdfApprovalSection } from './designSystem/reactPdfHelpers';
import { formatCurrency as fmtCAD } from '../utils/formatCurrency';

// ─── Label Map ────────────────────────────────────────────────
const labels = {
  en: {
    titleMain:      'SOUMISSION',
    titleSub:       'QUOTE',
    coLabel:        'N° / N°',
    dateLabel:      'Date',
    statusLabel:    'Status · Statut',
    projectLabel:   'Project · Projet',
    billTo:         'CLIENT · CLIENT',
    project:        'PROJECT · PROJET',
    infoLabels: {
      name:           'Name · Nom',
      company:        'Company · Entreprise',
      address:        'Address · Adresse',
      phone:          'Phone · Téléphone',
      email:          'Email · Courriel',
      projectName:    'Project · Projet',
      projectNumber:  'No. · N° projet',
      site:           'Site · Adresse',
      pm:             'PM · Chargé',
    },
    description:    'DESCRIPTION',
    costDetails:    'COST DETAILS',
    profit:         'Profit',
    subtotal:       'Subtotal',
    total:          'TOTAL',
    signDate:       'Date',
    noDescription:  'No description provided.',
    subcontract:    'Subcontractor',
    amount:         'Amount',
    statusLabels: {
      draft:     'Draft',
      submitted: 'Submitted',
      to_resubmit: 'To resubmit',
      approved:  'Approved',
      rejected:  'Rejected',
    },
    page: (n, t) => `Page ${n} of ${t}`,
  },
  fr: {
    titleMain:      'SOUMISSION',
    titleSub:       'QUOTE',
    coLabel:        'N° / N°',
    dateLabel:      'Date',
    statusLabel:    'Statut · Status',
    projectLabel:   'Projet · Project',
    billTo:         'CLIENT · CLIENT',
    project:        'PROJET · PROJECT',
    infoLabels: {
      name:           'Nom · Name',
      company:        'Entreprise · Company',
      address:        'Adresse · Address',
      phone:          'Téléphone · Phone',
      email:          'Courriel · Email',
      projectName:    'Projet · Project',
      projectNumber:  'N° projet · No.',
      site:           'Adresse · Site',
      pm:             'Chargé · PM',
    },
    description:    'DESCRIPTION',
    costDetails:    'DÉTAIL DES COÛTS',
    profit:         'Profit',
    subtotal:       'Sous-total',
    total:          'TOTAL',
    signDate:       'Date',
    noDescription:  'Aucune description fournie.',
    subcontract:    'Sous-traitant',
    amount:         'Montant',
    statusLabels: {
      draft:     'Brouillon',
      submitted: 'Soumis',
      to_resubmit: 'À resoumettre',
      approved:  'Approuvé',
      rejected:  'Rejeté',
    },
    page: (n, t) => `Page ${n} sur ${t}`,
  },
};

// ─── Styles ───────────────────────────────────────────────────
const styles = StyleSheet?.create({

  // ── Page ──
  page: {
    fontFamily: 'Helvetica',
    backgroundColor: COLORS?.white,
    paddingHorizontal: SPACING?.pageMarginPt,
    paddingTop: 32,
    paddingBottom: 44,
  },

  // ── Company name fallback (no-logo path only) ──
  companyNameLarge: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 16,
    color: COLORS?.black,
    marginBottom: 4,
  },

  // ── Body ──
  body: {
    paddingTop: 12,
  },

  // ── Description box ──
  descriptionSection: {
    marginBottom: 16,
  },
  descriptionBox: {
    borderWidth: 1,
    borderColor: COLORS?.gray300,
    borderRadius: 5,
    padding: 10,
    backgroundColor: COLORS?.white,
    minHeight: 50,
  },
  descriptionText: {
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: COLORS?.gray700,
    lineHeight: 1.4,
  },

  // ── Cost Details section ──
  costSection: {
    marginBottom: 16,
  },
  costTable: {
    borderWidth: 1,
    borderColor: COLORS?.gray300,
    borderRadius: 5,
    overflow: 'hidden',
  },
  // ── Dark DP-style table header ──
  costTableHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS?.black,
    borderBottomWidth: 1,
    borderBottomColor: COLORS?.gray300,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  costTableHeaderSubcontract: {
    flex: 1,
    fontFamily: 'Helvetica-Bold',
    fontSize: 7.5,
    color: COLORS?.offWhite,
    letterSpacing: 1,
  },
  costTableHeaderAmount: {
    width: 90,
    fontFamily: 'Helvetica-Bold',
    fontSize: 7.5,
    color: COLORS?.offWhite,
    letterSpacing: 1,
    textAlign: 'right',
  },

  // ── Subcontractor group header ──
  groupHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: COLORS?.gray50,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS?.gray300,
  },
  groupHeaderName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: COLORS?.black,
    flex: 1,
  },

  // ── Sub-line items ──
  subLineItem: {
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS?.gray200,
    paddingHorizontal: 20,
    paddingTop: 6,
    paddingBottom: 6,
  },
  subLineItemLast: {
    paddingHorizontal: 20,
    paddingTop: 6,
    paddingBottom: 6,
  },
  subLineRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
  },
  subLineDescription: {
    fontSize: 7.5,
    color: COLORS?.gray700,
    flex: 1,
    lineHeight: 1.4,
  },
  subLineAmount: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    width: 90,
    textAlign: 'right',
  },

  // ── Financial summary block ──
  financialSummary: {
    alignItems: 'flex-end',
    marginTop: 10,
    marginBottom: 20,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: 5,
    minWidth: 220,
  },
  summaryLabel: {
    fontSize: 8.5,
    color: COLORS?.gray700,
    flex: 1,
    textAlign: 'left',
  },
  summaryValue: {
    fontSize: 8.5,
    color: COLORS?.gray700,
    width: 90,
    textAlign: 'right',
  },
  summaryDivider: {
    borderTopWidth: 1,
    borderTopColor: COLORS?.gray300,
    minWidth: 220,
    marginBottom: 5,
  },
  // ── Orange TOTAL pill (matches DP TOTAL NET) ──
  summaryTotalPill: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: COLORS?.orange,
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginTop: 2,
    minWidth: 220,
  },
  summaryTotalPillLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    color: COLORS?.white,
  },
  summaryTotalPillValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    color: COLORS?.white,
    textAlign: 'right',
  },
  // ── Credit TOTAL row (plain bold, red — no orange pill for credits) ──
  summaryTotalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: 5,
    minWidth: 220,
  },
  summaryTotalLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: COLORS?.black,
    flex: 1,
    textAlign: 'left',
    letterSpacing: 0.5,
  },
  summaryTotalValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: COLORS?.black,
    width: 90,
    textAlign: 'right',
  },
});

// ─── Helpers ──────────────────────────────────────────────────
const formatDate = (iso, language = 'en') => {
  if (!iso) return '—';
  try {
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    return new Date(iso)?.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

const formatCurrency = (value) => fmtCAD(value);

/**
 * Groups lines by subcontractName.
 * Returns: [{ name: string, lines: Line[] }, ...]
 */
const groupLinesBySubcontractor = (lines) => {
  const map = new Map();
  (lines || [])?.forEach((line) => {
    const key = line?.subcontractName || '—';
    if (!map?.has(key)) map?.set(key, []);
    map?.get(key)?.push(line);
  });
  return Array.from(map?.entries())?.map(([name, groupLines]) => ({ name, lines: groupLines }));
};

// ─── Extra Document ───────────────────────────────────────────
const ExtraDocument = ({
  extra,
  projectName,
  projectNumber,
  projectAddress,
  pmFullName,
  companyName,
  logoUrl,
  language = 'en',
  // alternate prop names
  project,
  company: companyProp,
  logoBase64,
  // org info props (new — from handleDownloadExtraPDF)
  tagline,
  orgAddress,
  orgPhone,
  orgEmail,
  orgWebsite,
  rbqNumber,
  tpsNumber,
  tvqNumber,
  // Client du projet — meme forme que PaymentRequestDocument :
  // [{ role, company_name, contact_person, address, contact_phone, contact_email }]
  clients = [],
  // Professionnels du Repertoire du projet — meme forme que PaymentRequestDocument :
  // [{ role, profession, company_name, contact_person, contact_phone, contact_email }]
  professionals = [],
}) => {
  const t = labels?.[language] || labels?.en;

  const company      = companyName || companyProp || '';
  const projName     = projectName || project?.name || '';
  const projNum      = projectNumber || project?.projectNumber || '';
  // Le champ location est volontairement ignore : il vaut « TBD » par defaut en base,
  // un mot qui ne doit jamais partir chez un client. L'adresse se compose des
  // quatre champs structures, comme dans PaymentRequestDocument.
  const projAddress  = projectAddress
    || [project?.address, project?.city, project?.state, project?.zipCode || project?.zip_code]?.filter(Boolean)?.join(', ')
    || '';
  // toCamelCase ne camelise que le premier niveau : l'objet pm imbrique reste
  // en snake_case. La cle pmFullName sur le projet n'a jamais existe.
  const pmName       = pmFullName || project?.pm?.fullName || project?.pm?.full_name || '';
  const coNum        = extra?.coNumber || extra?.co_number || '—';
  const logoSrc      = logoUrl || logoBase64 || null;

  const dateStr = formatDate(
    extra?.submittedDate || extra?.submitted_date ||
    extra?.receivedDate  || extra?.received_date  ||
    extra?.createdAt     || extra?.created_at,
    language
  );

  const rawStatus   = extra?.status || 'draft';
  const statusLabel = t?.statusLabels?.[rawStatus] || rawStatus;

  // Lines from extra.lines (injected by handleDownloadExtraPDF)
  const lines = extra?.lines || [];

  // Credit type flag
  const isCredit = extra?.type === 'credit';

  // Totals — data/calc UNCHANGED
  const base   = lines?.reduce((sum, l) => sum + (parseFloat(l?.amount) || 0), 0);
  const profit = parseFloat(extra?.profit) || 0;
  const totalRaw  = base + profit;

  // Display values — negative for credits
  const subtotalDisplay = isCredit ? -Math.abs(base) : base;
  const profitDisplay   = isCredit ? -Math.abs(profit) : profit;
  const totalDisplay    = isCredit ? -Math.abs(totalRaw) : totalRaw;

  // Color for credit amounts
  const amountColor = isCredit ? '#dc2626' : COLORS?.orange;
  const totalColor  = isCredit ? '#dc2626' : COLORS?.black;

  // Group lines by subcontractor for display
  const groups = groupLinesBySubcontractor(lines);

  // Build contact line (phone · email · website)
  const contactParts = [orgPhone, orgEmail, orgWebsite]?.filter(Boolean);
  const contactLine = contactParts?.join(' · ');

  // ── FACTURÉ À : le premier client rattache au projet ──
  // Meme forme et meme ordre de replis que PaymentRequestDocument. Une ligne sans
  // donnee est retiree, pas rendue avec un tiret.
  const firstClient = clients?.[0] || null;
  const billToRows = [
    { label: t?.infoLabels?.name,    value: firstClient?.contact_person || '' },
    { label: t?.infoLabels?.company, value: firstClient?.company_name || project?.clientName || project?.client_name || '' },
    { label: t?.infoLabels?.address, value: firstClient?.address || '' },
  ]?.filter(r => r?.value);

  // ── APPROBATION : une zone a signer par professionnel, le client en dernier ──
  // Identique a PaymentRequestDocument : les professionnels viennent du Repertoire
  // du projet, le client ferme la marche en carte accentuee. Une donnee manquante
  // laisse la ligne a signer vide plutot qu'une etiquette orpheline ou un tiret.
  const approvalSectionLabel = 'APPROBATION · APPROVAL';
  const signatureLabel = 'Signature';
  const clientRoleLabel = 'CLIENT · CLIENT';

  const professionalCards = (professionals || [])?.map(prof => ({
    role: prof?.role || prof?.profession || '',
    name: prof?.contact_person || prof?.company_name || '',
    info: [prof?.company_name, prof?.contact_phone]?.filter(Boolean)?.join(' · '),
    accent: false,
  }));

  const clientCard = {
    role: clientRoleLabel,
    name: firstClient?.contact_person || firstClient?.company_name || project?.clientName || project?.client_name || '',
    info: [firstClient?.company_name, firstClient?.contact_phone]?.filter(Boolean)?.join(' · '),
    accent: true,
  };

  const approvalCards = [...professionalCards, clientCard];

  // Build tax info line (RBQ · TPS · TVQ)
  const taxParts = [
    rbqNumber ? `RBQ : ${rbqNumber}` : null,
    tpsNumber ? `TPS : ${tpsNumber}` : null,
    tvqNumber ? `TVQ : ${tvqNumber}` : null,
  ]?.filter(Boolean);
  const taxLine = taxParts?.join(' · ');

  return (
    <Document>
      <Page size="A4" style={styles?.page}>

        {/* ── PHASE 2: DP-style PdfHeader ── */}
        <PdfHeader
          logoBase64={logoSrc}
          docTitle={t?.titleMain}
          docSubtitle={t?.titleSub}
          meta={[
            { label: t?.projectLabel, value: projNum ? `${projNum}` : projName || '—' },
            { label: t?.coLabel,      value: coNum },
            { label: t?.dateLabel,    value: dateStr },
            { label: t?.statusLabel,  value: statusLabel },
          ]}
          tagline={tagline}
          address={orgAddress}
          contactLine={contactLine}
          taxLine={taxLine}
        />

        {/* ── PHASE 3: PdfInfoColumns — PROJET + FACTURÉ À ── */}
        <PdfInfoColumns
          columns={[
            {
              title: t?.project,
              rows: [
                { label: t?.infoLabels?.projectName,   value: projName || '—' },
                { label: t?.infoLabels?.projectNumber, value: projNum || '—' },
                { label: t?.infoLabels?.site,          value: projAddress || '—' },
                { label: t?.infoLabels?.pm,            value: pmName || '—' },
              ],
            },
            // Le bloc client n'apparait que s'il a de quoi se remplir, et chaque
            // ligne est omise quand sa donnee manque : une etiquette suivie d'un
            // tiret sur un document qui part chez le client ne dit rien de bon.
            ...(billToRows?.length > 0
              ? [{ title: t?.billTo, rows: billToRows }]
              : []),
          ]}
        />

        {/* ── Body ── */}
        <View style={styles?.body}>

          {/* ── Main Description ── */}
          {(extra?.title || extra?.description) ? (
            <View style={styles?.descriptionSection}>
              <PdfSectionTitle label={t?.description} />
              <View style={styles?.descriptionBox}>
                {extra?.title ? (
                  <Text style={styles?.descriptionText}>{extra?.title}</Text>
                ) : null}
                {extra?.description ? (
                  <Text style={[styles?.descriptionText, extra?.title ? { marginTop: 4 } : {}]}>
                    {extra?.description}
                  </Text>
                ) : null}
              </View>
            </View>
          ) : null}

          {/* ── PHASE 4: Cost Details — grouped by subcontractor, dark DP header ── */}
          <View style={styles?.costSection}>
            <PdfSectionTitle label={t?.costDetails} />
            <View style={styles?.costTable}>

              {/* Dark DP-style table header */}
              <View style={styles?.costTableHeader}>
                <Text style={styles?.costTableHeaderSubcontract}>{t?.subcontract}</Text>
                <Text style={styles?.costTableHeaderAmount}>{t?.amount}</Text>
              </View>

              {/* Groups — custom layout preserved (PdfItemsTable cannot reproduce data-row groups) */}
              {groups?.length > 0 ? groups?.map((group, gIdx) => (
                <View key={gIdx}>
                  {/* Group header row — subcontractor name */}
                  <View style={styles?.groupHeader}>
                    <Text style={styles?.groupHeaderName}>{group?.name}</Text>
                  </View>

                  {/* Sub-lines */}
                  {group?.lines?.map((line, lIdx) => {
                    // Ligne stockee relative au type, parfois negative : vrai signe.
                    const signedAmount = isCredit
                      ? -(parseFloat(line?.amount) || 0)
                      : (parseFloat(line?.amount) || 0);
                    const isLastLine = lIdx === group?.lines?.length - 1;
                    return (
                      <View
                        key={lIdx}
                        style={isLastLine ? styles?.subLineItemLast : styles?.subLineItem}
                      >
                        <View style={styles?.subLineRow}>
                          <Text style={styles?.subLineDescription}>
                            {line?.description || '—'}
                          </Text>
                          <Text style={[styles?.subLineAmount, { color: amountColor }]}>
                            {formatCurrency(signedAmount)}
                          </Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              )) : (
                <View style={styles?.subLineItemLast}>
                  <Text style={styles?.subLineDescription}>—</Text>
                </View>
              )}

            </View>
          </View>

          {/* ── PHASE 4: Financial Summary — orange TOTAL pill (extras) / plain bold (credits) ── */}
          <View style={styles?.financialSummary}>
            {/* Subtotal row */}
            <View style={styles?.summaryRow}>
              <Text style={styles?.summaryLabel}>{t?.subtotal}</Text>
              <Text style={[styles?.summaryValue, { color: amountColor }]}>
                {formatCurrency(subtotalDisplay)}
              </Text>
            </View>

            {/* Profit row — only if profit > 0 */}
            {profit > 0 ? (
              <View style={styles?.summaryRow}>
                <Text style={styles?.summaryLabel}>
                  {t?.profit}{base > 0 ? ` (${Math.round((profit / base) * 100)}%)` : ''}
                </Text>
                <Text style={[styles?.summaryValue, { color: amountColor }]}>
                  {formatCurrency(profitDisplay)}
                </Text>
              </View>
            ) : null}

            {/* Divider */}
            <View style={styles?.summaryDivider} />

            {/* TOTAL — orange pill for extras, plain bold red for credits */}
            {isCredit ? (
              <View style={styles?.summaryTotalRow}>
                <Text style={styles?.summaryTotalLabel}>{t?.total}</Text>
                <Text style={[styles?.summaryTotalValue, { color: totalColor }]}>
                  {formatCurrency(totalDisplay)}
                </Text>
              </View>
            ) : (
              <View style={styles?.summaryTotalPill}>
                <Text style={styles?.summaryTotalPillLabel}>{t?.total}</Text>
                <Text style={styles?.summaryTotalPillValue}>
                  {formatCurrency(totalDisplay)}
                </Text>
              </View>
            )}

          </View>

          {/* ── Approbation — meme bloc que la demande de paiement ── */}
          <PdfApprovalSection
            title={approvalSectionLabel}
            cards={approvalCards}
            signatureLabel={signatureLabel}
            dateLabel={t?.signDate}
          />

        </View>

        {/* ── PHASE 2: PdfFooter — "By OnsitePM" left, Page X/Y right ── */}
        <PdfFooter
          leftText="By OnsitePM"
          renderRight={({ pageNumber, totalPages }) => t?.page(pageNumber, totalPages)}
        />

      </Page>
    </Document>
  );
};

export default ExtraDocument;
